import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import {
  TrendingUp, TrendingDown, DollarSign, FileText, Percent,
  ArrowUpRight, ArrowDownRight, Calendar, Layers, ShoppingBag,
  Download, Printer, ChevronRight, Info, RefreshCw, Tag,
  Receipt, Users, Clock, BarChart2, Wifi, MapPin, UserCheck, Timer,
} from "lucide-react";
import api from "../../utils/api";
import { CSVLink } from "react-csv";

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler
);

const ORDER_TYPE_LABELS = { DINE_IN: "Dine-In", DIRECT: "Takeaway", ONLINE: "Online" };

const GrowthBadge = ({ growth, positive = true }) => {
  const isUp = growth >= 0;
  const good = positive ? isUp : !isUp;
  return (
    <span className={`inline-flex items-center text-xs font-medium ${good ? "text-emerald-600" : "text-rose-600"}`}>
      {isUp ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
      {isUp ? "+" : ""}{growth}%
    </span>
  );
};

// Simple linear regression — returns next `steps` predicted values
const linearRegression = (points, steps = 7) => {
  const n = points.length;
  if (n < 2) return Array(steps).fill(null);
  const xs = points.map((_, i) => i);
  const ys = points.map(p => p.revenue);
  const xMean = xs.reduce((a, b) => a + b, 0) / n;
  const yMean = ys.reduce((a, b) => a + b, 0) / n;
  const denom = xs.reduce((acc, x) => acc + (x - xMean) ** 2, 0);
  if (denom === 0) return Array(steps).fill(yMean);
  const slope = xs.reduce((acc, x, i) => acc + (x - xMean) * (ys[i] - yMean), 0) / denom;
  const intercept = yMean - slope * xMean;
  return Array.from({ length: steps }, (_, i) => Math.max(0, Math.round(slope * (n + i) + intercept)));
};

const addDays = (dateStr, days) => {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const AnalyticsDashboard = () => {
  const [filter, setFilter] = useState("7days");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [showPrior, setShowPrior] = useState(false);
  const [showForecast, setShowForecast] = useState(false);
  const autoRefreshRef = useRef(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { filter };
      if (filter === "custom") {
        if (!fromDate || !toDate) { setLoading(false); return; }
        params.fromDate = fromDate;
        params.toDate = toDate;
      }
      const response = await api.get("/analytics/merchant-stats", { params });
      if (response.data.success) {
        setData(response.data.data);
        setLastRefreshed(new Date());
      } else {
        setError(response.data.message || "Failed to load analytics");
      }
    } catch (err) {
      console.error("Fetch analytics error:", err);
      setError("Failed to fetch analytics. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [filter, fromDate, toDate]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  useEffect(() => {
    if (autoRefresh) {
      autoRefreshRef.current = setInterval(fetchAnalytics, 30000);
    } else {
      clearInterval(autoRefreshRef.current);
    }
    return () => clearInterval(autoRefreshRef.current);
  }, [autoRefresh, fetchAnalytics]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center mt-14">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 font-medium">Compiling business intelligence report...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center mt-14 px-4">
        <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center text-red-600 mx-auto mb-4">
            <Info className="w-6 h-6" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Analytics Error</h2>
          <p className="text-gray-500 mb-6">{error || "No dashboard data returned"}</p>
          <button onClick={fetchAnalytics} className="w-full py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition">
            Retry Fetching Data
          </button>
        </div>
      </div>
    );
  }

  const {
    kpis, salesTrend, priorSalesTrend, paymentMethods, topItems,
    categoryPerformance, orderTypeBreakdown, peakHours, dayOfWeek,
    topCustomers, qrVsAdmin, repeatVsNew, floorUtilization, platformBreakdown,
  } = data;

  // ── Sales Trend chart (with prior period overlay + forecast) ────────────────

  const lastTrendId = salesTrend.length > 0 ? salesTrend[salesTrend.length - 1]._id : null;
  const canForecast = filter !== "today" && salesTrend.length > 1 && lastTrendId && !isNaN(new Date(lastTrendId).getTime());
  const FORECAST_STEPS = 7;
  const forecastValues = canForecast ? linearRegression(salesTrend, FORECAST_STEPS) : [];
  const forecastLabels = canForecast
    ? Array.from({ length: FORECAST_STEPS }, (_, i) => addDays(lastTrendId, i + 1)).filter(Boolean)
    : [];

  const allLabels = showForecast && canForecast
    ? [...salesTrend.map(t => t._id), ...forecastLabels]
    : salesTrend.map(t => t._id);

  const lineDatasets = [
    {
      fill: true,
      label: "Revenue (₹)",
      data: showForecast && canForecast
        ? [...salesTrend.map(t => t.revenue), ...Array(FORECAST_STEPS).fill(null)]
        : salesTrend.map(t => t.revenue),
      borderColor: "rgb(99,102,241)",
      backgroundColor: "rgba(99,102,241,0.1)",
      tension: 0.35,
      yAxisID: "y",
      pointRadius: 3,
    },
    {
      label: "Bills",
      data: showForecast && canForecast
        ? [...salesTrend.map(t => t.ordersCount), ...Array(FORECAST_STEPS).fill(null)]
        : salesTrend.map(t => t.ordersCount),
      borderColor: "rgb(20,184,166)",
      backgroundColor: "rgba(20,184,166,0.1)",
      tension: 0.35,
      yAxisID: "y1",
      pointRadius: 3,
    },
  ];

  if (showPrior && priorSalesTrend?.length > 0) {
    const padded = [...priorSalesTrend.map(t => t.revenue)];
    while (padded.length < allLabels.length) padded.push(null);
    lineDatasets.push({
      label: "Prior Period Revenue (₹)",
      data: padded,
      borderColor: "rgba(156,163,175,0.8)",
      backgroundColor: "transparent",
      borderDash: [5, 4],
      tension: 0.35,
      yAxisID: "y",
      pointRadius: 2,
      pointBackgroundColor: "rgba(156,163,175,0.6)",
    });
  }

  if (showForecast && canForecast) {
    lineDatasets.push({
      label: "Revenue Forecast (₹)",
      data: [...Array(salesTrend.length).fill(null), ...forecastValues],
      borderColor: "rgba(99,102,241,0.45)",
      backgroundColor: "transparent",
      borderDash: [6, 4],
      tension: 0.35,
      yAxisID: "y",
      pointRadius: 0,
    });
  }

  const lineChartData = { labels: allLabels, datasets: lineDatasets };

  const lineChartOptions = {
    responsive: true,
    interaction: { mode: "index", intersect: false },
    plugins: { legend: { position: "top", labels: { usePointStyle: true, font: { family: "Inter, sans-serif", weight: 600 } } } },
    scales: {
      y:  { type: "linear", display: true, position: "left",  grid: { color: "rgba(243,244,246,1)" }, ticks: { callback: v => `₹${v}` } },
      y1: { type: "linear", display: true, position: "right", grid: { drawOnChartArea: false } },
    },
  };

  const PALETTE = ["rgba(99,102,241,0.8)", "rgba(20,184,166,0.8)", "rgba(245,158,11,0.8)", "rgba(239,68,68,0.8)", "rgba(168,85,247,0.8)", "rgba(107,114,128,0.8)"];

  const doughnutChartData = {
    labels: paymentMethods.map(pm => pm._id),
    datasets: [{ data: paymentMethods.map(pm => pm.revenue), backgroundColor: PALETTE, borderWidth: 2 }],
  };
  const doughnutOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "right", labels: { boxWidth: 12 } } } };

  const orderTypeData = {
    labels: orderTypeBreakdown.map(o => ORDER_TYPE_LABELS[o._id] || o._id || "Other"),
    datasets: [{ data: orderTypeBreakdown.map(o => o.revenue), backgroundColor: PALETTE, borderWidth: 2 }],
  };

  // ── QR vs Admin ─────────────────────────────────────────────────────────────
  const SOURCE_LABELS = { ADMIN: "POS / Admin", QR: "QR Scan", KIOSK: "Kiosk" };
  const SOURCE_COLORS = ["rgba(99,102,241,0.8)", "rgba(20,184,166,0.8)", "rgba(245,158,11,0.8)"];
  const qrAdminData = {
    labels: (qrVsAdmin || []).map(s => SOURCE_LABELS[s._id] || s._id || "Unknown"),
    datasets: [{ data: (qrVsAdmin || []).map(s => s.count), backgroundColor: SOURCE_COLORS, borderWidth: 2 }],
  };

  // ── Category bar ────────────────────────────────────────────────────────────
  const barChartData = {
    labels: categoryPerformance.map(c => c._id),
    datasets: [{ label: "Revenue (₹)", data: categoryPerformance.map(c => c.revenue), backgroundColor: "rgba(99,102,241,0.75)", hoverBackgroundColor: "rgba(99,102,241,0.9)", borderRadius: 8 }],
  };
  const barChartOptions = {
    responsive: true,
    plugins: { legend: { display: false } },
    scales: { x: { grid: { display: false } }, y: { grid: { color: "rgba(243,244,246,1)" }, ticks: { callback: v => `₹${v}` } } },
  };

  // ── Peak hours ───────────────────────────────────────────────────────────────
  const peakHoursData = {
    labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
    datasets: [{
      label: "Bills",
      data: Array.from({ length: 24 }, (_, i) => peakHours.find(h => h.hour === i)?.count || 0),
      backgroundColor: (ctx) => {
        const val = ctx.raw;
        const max = Math.max(...ctx.chart.data.datasets[0].data);
        const ratio = max > 0 ? val / max : 0;
        return `rgba(99,102,241,${0.2 + ratio * 0.75})`;
      },
      borderRadius: 6,
    }],
  };
  const peakHoursOptions = {
    responsive: true,
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ` ${ctx.raw} bills` } } },
    scales: { x: { grid: { display: false }, ticks: { maxRotation: 0, font: { size: 10 } } }, y: { grid: { color: "rgba(243,244,246,1)" }, ticks: { stepSize: 1 } } },
  };

  // ── Day of week ──────────────────────────────────────────────────────────────
  const DAY_ORDER = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const dowData = {
    labels: DAY_ORDER,
    datasets: [{ label: "Revenue (₹)", data: DAY_ORDER.map((_, i) => dayOfWeek.find(d => d.day === i + 1)?.revenue || 0), backgroundColor: "rgba(20,184,166,0.75)", hoverBackgroundColor: "rgba(20,184,166,0.9)", borderRadius: 8 }],
  };
  const dowOptions = {
    responsive: true,
    plugins: { legend: { display: false } },
    scales: { x: { grid: { display: false } }, y: { grid: { color: "rgba(243,244,246,1)" }, ticks: { callback: v => `₹${v}` } } },
  };

  // ── Floor utilization ────────────────────────────────────────────────────────
  const floorData = {
    labels: (floorUtilization || []).map(f => f._id),
    datasets: [{ label: "Revenue (₹)", data: (floorUtilization || []).map(f => f.revenue), backgroundColor: "rgba(245,158,11,0.75)", hoverBackgroundColor: "rgba(245,158,11,0.9)", borderRadius: 6 }],
  };
  const floorOptions = {
    indexAxis: "y",
    responsive: true,
    plugins: { legend: { display: false } },
    scales: { x: { grid: { color: "rgba(243,244,246,1)" }, ticks: { callback: v => `₹${v}` } }, y: { grid: { display: false } } },
  };

  // ── Repeat vs New ────────────────────────────────────────────────────────────
  const rv = repeatVsNew || { repeat: 0, newCustomers: 0 };
  const totalCust = rv.repeat + rv.newCustomers;
  const repeatPct  = totalCust > 0 ? Math.round((rv.repeat / totalCust) * 100) : 0;
  const newPct     = totalCust > 0 ? 100 - repeatPct : 0;
  const repeatVsNewData = {
    labels: ["Repeat Customers", "New Customers"],
    datasets: [{ data: [rv.repeat, rv.newCustomers], backgroundColor: ["rgba(99,102,241,0.8)", "rgba(20,184,166,0.8)"], borderWidth: 2 }],
  };

  // ── CSV export ───────────────────────────────────────────────────────────────
  const csvData = [
    { metric: "Gross Revenue", value: `₹${kpis.revenue.value}` },
    { metric: "Bills Count", value: kpis.billsCount.value },
    { metric: "Average Bill Size", value: `₹${kpis.avgBillValue.value}` },
    { metric: "Total Expenses", value: `₹${kpis.expenses.value}` },
    { metric: "Net Profit", value: `₹${kpis.netProfit.value}` },
    { metric: "Total Discount Given", value: `₹${kpis.totalDiscount.value}` },
    { metric: "Total GST Collected", value: `₹${kpis.totalGST.value}` },
    { metric: "Avg Service Time", value: kpis.avgServiceMins?.value != null ? `${kpis.avgServiceMins.value} min` : "N/A" },
  ];
  const csvHeaders = [{ label: "Metric", key: "metric" }, { label: "Value", key: "value" }];

  return (
    <div className="min-h-screen bg-slate-50 pt-20 pb-16 px-4 sm:px-6 lg:px-8 print:p-0 print:bg-white">

      {/* ── Header & Toolbar ── */}
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 print:hidden">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Business Intelligence</h1>
          <p className="text-gray-500 mt-1 text-sm">
            Real-time analytics &amp; sales performance charts.
            {lastRefreshed && <span className="ml-2 text-gray-400">Last updated: {lastRefreshed.toLocaleTimeString()}</span>}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="bg-white p-1 rounded-xl shadow-sm border border-gray-200 flex gap-1">
            {[{ id: "today", label: "Today" }, { id: "7days", label: "7 Days" }, { id: "30days", label: "30 Days" }, { id: "thismonth", label: "This Month" }, { id: "custom", label: "Custom" }].map(btn => (
              <button key={btn.id} onClick={() => { setLoading(true); setFilter(btn.id); }}
                className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${filter === btn.id ? "bg-indigo-600 text-white shadow-sm" : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"}`}>
                {btn.label}
              </button>
            ))}
          </div>

          <button onClick={() => setAutoRefresh(v => !v)}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold border transition-all ${autoRefresh ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
            <RefreshCw className={`w-3.5 h-3.5 ${autoRefresh ? "animate-spin" : ""}`} />
            {autoRefresh ? "Live" : "Auto-Refresh"}
          </button>

          <button onClick={fetchAnalytics} className="p-2.5 bg-white border border-gray-200 rounded-xl shadow-sm text-gray-600 hover:bg-gray-50 transition" title="Refresh now">
            <RefreshCw className="w-4 h-4" />
          </button>

          <CSVLink data={csvData} headers={csvHeaders} filename={`CR-Analytics-${filter}-${new Date().toISOString().slice(0, 10)}.csv`}
            className="p-2.5 bg-white border border-gray-200 rounded-xl shadow-sm text-gray-700 hover:bg-gray-50 transition flex items-center gap-2 text-sm font-semibold">
            <Download className="w-4 h-4" /><span className="hidden sm:inline">Export CSV</span>
          </CSVLink>

          <button onClick={() => window.print()} className="p-2.5 bg-indigo-600 rounded-xl shadow-sm text-white hover:bg-indigo-700 transition flex items-center gap-2 text-sm font-semibold">
            <Printer className="w-4 h-4" /><span className="hidden sm:inline">Print</span>
          </button>
        </div>
      </div>

      {/* ── Custom date picker ── */}
      {filter === "custom" && (
        <div className="max-w-7xl mx-auto mb-8 bg-white p-5 rounded-2xl shadow-sm border border-gray-200 print:hidden">
          <form onSubmit={e => { e.preventDefault(); fetchAnalytics(); }} className="flex flex-wrap items-end gap-4">
            {[{ label: "From Date", val: fromDate, set: setFromDate }, { label: "To Date", val: toDate, set: setToDate }].map(f => (
              <div key={f.label}>
                <label className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-2">{f.label}</label>
                <div className="relative">
                  <Calendar className="w-4 h-4 text-gray-400 absolute left-3 top-3.5" />
                  <input type="date" value={f.val} onChange={e => f.set(e.target.value)} required
                    className="pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
              </div>
            ))}
            <button type="submit" className="px-6 py-3 bg-gray-900 text-white rounded-xl text-sm font-bold hover:bg-gray-800 transition">Apply Filter</button>
          </form>
        </div>
      )}

      {/* ── KPI Cards (5) ── */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 mb-5">
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-700 p-6 rounded-2xl shadow-md text-white flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <span className="text-sm font-semibold text-indigo-100">Gross Revenue</span>
            <div className="p-2 bg-indigo-400/20 rounded-xl"><DollarSign className="w-5 h-5 text-white" /></div>
          </div>
          <div className="mt-4">
            <span className="text-3xl font-extrabold">₹{kpis.revenue.value.toLocaleString()}</span>
            <div className="flex items-center gap-1 mt-2 text-xs font-medium">
              <GrowthBadge growth={kpis.revenue.growth} />
              <span className="text-indigo-200 opacity-90">vs last period</span>
            </div>
          </div>
        </div>

        {[
          { label: "Total Bills",  value: kpis.billsCount.value,  growth: kpis.billsCount.growth, icon: <FileText className="w-5 h-5" />,    iconBg: "bg-teal-50 text-teal-600",    format: v => v },
          { label: "Avg Bill Size",value: kpis.avgBillValue.value, icon: <Percent className="w-5 h-5" />,   iconBg: "bg-amber-50 text-amber-600",  format: v => `₹${v}`, sub: "Avg per checkout" },
          { label: "Expenses",     value: kpis.expenses.value,    growth: kpis.expenses.growth,   icon: <TrendingDown className="w-5 h-5" />, iconBg: "bg-orange-50 text-orange-600", format: v => `₹${v.toLocaleString()}`, inverseGrowth: true },
          { label: "Net Profit",   value: kpis.netProfit.value,   growth: kpis.netProfit.growth,  icon: <TrendingUp className="w-5 h-5" />,   iconBg: "bg-emerald-50 text-emerald-600", format: v => `₹${v.toLocaleString()}`, valueColor: kpis.netProfit.value >= 0 ? "text-emerald-600" : "text-rose-600" },
        ].map(card => (
          <div key={card.label} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all flex flex-col justify-between">
            <div className="flex justify-between items-start">
              <span className="text-sm font-semibold text-gray-500">{card.label}</span>
              <div className={`p-2 rounded-xl ${card.iconBg}`}>{card.icon}</div>
            </div>
            <div className="mt-4">
              <span className={`text-3xl font-extrabold ${card.valueColor || "text-gray-900"}`}>{card.format(card.value)}</span>
              {card.growth !== undefined ? (
                <div className="flex items-center gap-1 mt-2 text-xs font-medium">
                  <GrowthBadge growth={card.growth} positive={!card.inverseGrowth} />
                  <span className="text-gray-400">vs last period</span>
                </div>
              ) : (
                <p className="text-xs text-gray-400 mt-2 font-medium">{card.sub}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ── Financial Summary Strip (Discount / GST / Platform Fee / Avg Service) ── */}
      <div className="max-w-7xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Total Discount Given",    value: `₹${kpis.totalDiscount.value.toLocaleString()}`,    icon: <Tag className="w-4 h-4" />,      color: "text-rose-600",   bg: "bg-rose-50"   },
          { label: "Total GST Collected",     value: `₹${kpis.totalGST.value.toLocaleString()}`,        icon: <Receipt className="w-4 h-4" />,  color: "text-blue-600",   bg: "bg-blue-50"   },
          { label: "Platform Fee Collected",  value: `₹${kpis.totalPlatformFee.value.toLocaleString()}`,icon: <BarChart2 className="w-4 h-4" />, color: "text-purple-600", bg: "bg-purple-50" },
          { label: "Avg Service Time",        value: kpis.avgServiceMins?.value != null ? `${kpis.avgServiceMins.value} min` : "N/A", icon: <Timer className="w-4 h-4" />, color: "text-amber-600", bg: "bg-amber-50" },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4 flex items-center gap-4">
            <div className={`p-2.5 rounded-xl ${s.bg} ${s.color}`}>{s.icon}</div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{s.label}</p>
              <p className={`text-xl font-extrabold ${s.color}`}>{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Row 2: Sales Trend (with prior + forecast toggles) ── */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h3 className="text-lg font-bold text-gray-900">Revenue &amp; Bills Timeline</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowPrior(v => !v)}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all ${showPrior ? "bg-gray-700 text-white border-gray-700" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}>
                Prior Period
              </button>
              {canForecast && (
                <button
                  onClick={() => setShowForecast(v => !v)}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all ${showForecast ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}>
                  7-Day Forecast
                </button>
              )}
            </div>
          </div>
          <div className="h-72">
            {salesTrend.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-400 text-sm">No trend data for this range.</div>
            ) : (
              <Line data={lineChartData} options={lineChartOptions} />
            )}
          </div>
          {showForecast && canForecast && (
            <p className="text-xs text-gray-400 mt-3 text-right">Dashed line = linear regression projection. Actual results may vary.</p>
          )}
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col gap-6">
          <div>
            <h3 className="text-base font-bold text-gray-900 mb-4">Payment Distribution</h3>
            {paymentMethods.length === 0 ? (
              <div className="h-36 flex items-center justify-center text-gray-400 text-sm">No payment data.</div>
            ) : (
              <div className="h-36"><Doughnut data={doughnutChartData} options={doughnutOptions} /></div>
            )}
          </div>

          <div className="border-t border-gray-100 pt-4">
            <h3 className="text-base font-bold text-gray-900 mb-4">Order Type Split</h3>
            {orderTypeBreakdown.length === 0 ? (
              <div className="h-28 flex items-center justify-center text-gray-400 text-sm">No order type data.</div>
            ) : (
              <div className="h-28"><Doughnut data={orderTypeData} options={doughnutOptions} /></div>
            )}
          </div>

          {/* Platform breakdown — only when there are ONLINE orders */}
          {platformBreakdown && platformBreakdown.length > 0 && (
            <div className="border-t border-gray-100 pt-4">
              <h3 className="text-base font-bold text-gray-900 mb-3">Online Platform Split</h3>
              <div className="space-y-2">
                {(() => {
                  const PLAT_COLORS = { SWIGGY: "#f97316", ZOMATO: "#ef4444", MANUAL: "#6b7280" };
                  const total = platformBreakdown.reduce((s, p) => s + p.revenue, 0) || 1;
                  return platformBreakdown.map((p) => {
                    const pct = Math.round((p.revenue / total) * 100);
                    const color = PLAT_COLORS[p._id] || "#6b7280";
                    return (
                      <div key={p._id}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: color }} />
                            <span className="font-semibold text-gray-700">{p._id}</span>
                            <span className="text-gray-400">· {p.count} order{p.count !== 1 ? "s" : ""}</span>
                          </div>
                          <span className="font-bold" style={{ color }}>₹{p.revenue.toLocaleString("en-IN")} ({pct}%)</span>
                        </div>
                        <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Row 3: QR vs Admin + Repeat vs New customers ── */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">

        {/* QR vs Admin source split */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-6">
            <Wifi className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg font-bold text-gray-900">Order Source</h3>
            <span className="ml-auto text-xs text-gray-400">QR Scan vs POS</span>
          </div>
          {!qrVsAdmin || qrVsAdmin.length === 0 ? (
            <div className="h-40 flex items-center justify-center text-gray-400 text-sm">No source data available.</div>
          ) : (
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="h-44 w-44 flex-shrink-0">
                <Doughnut data={qrAdminData} options={{ responsive: true, maintainAspectRatio: true, plugins: { legend: { display: false } } }} />
              </div>
              <div className="flex flex-col gap-3 flex-1">
                {qrVsAdmin.map((s, i) => {
                  const total = qrVsAdmin.reduce((a, b) => a + b.count, 0);
                  const pct = total > 0 ? Math.round((s.count / total) * 100) : 0;
                  return (
                    <div key={s._id} className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: SOURCE_COLORS[i] || "#ccc" }}></div>
                        <span className="text-sm font-semibold text-gray-700">{SOURCE_LABELS[s._id] || s._id}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: SOURCE_COLORS[i] || "#ccc" }}></div>
                        </div>
                        <span className="text-xs font-bold text-gray-500 w-10 text-right">{pct}%</span>
                        <span className="text-xs text-gray-400">{s.count} bills</span>
                      </div>
                    </div>
                  );
                })}
                <p className="text-xs text-gray-400 mt-2">Revenue by source shown in breakdown above.</p>
              </div>
            </div>
          )}
        </div>

        {/* Repeat vs New customer ratio */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-6">
            <UserCheck className="w-5 h-5 text-teal-600" />
            <h3 className="text-lg font-bold text-gray-900">Customer Loyalty</h3>
            <span className="ml-auto text-xs text-gray-400">Repeat vs New</span>
          </div>
          {totalCust === 0 ? (
            <div className="h-40 flex items-center justify-center text-gray-400 text-sm">No customer-linked bills this period.</div>
          ) : (
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="h-44 w-44 flex-shrink-0">
                <Doughnut data={repeatVsNewData} options={{ responsive: true, maintainAspectRatio: true, plugins: { legend: { display: false } } }} />
              </div>
              <div className="flex flex-col gap-4 flex-1">
                {[
                  { label: "Repeat Customers", count: rv.repeat, pct: repeatPct, color: "rgba(99,102,241,0.8)", note: "Visited before this period" },
                  { label: "New Customers",    count: rv.newCustomers, pct: newPct, color: "rgba(20,184,166,0.8)", note: "First time this period" },
                ].map(s => (
                  <div key={s.label}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }}></div>
                        <span className="text-sm font-semibold text-gray-700">{s.label}</span>
                      </div>
                      <span className="text-sm font-bold text-gray-900">{s.count} <span className="text-xs font-medium text-gray-400">({s.pct}%)</span></span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${s.pct}%`, backgroundColor: s.color }}></div>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">{s.note}</p>
                  </div>
                ))}
                <p className="text-xs font-semibold text-gray-500 border-t border-gray-100 pt-3">
                  Loyalty Rate: <span className="text-indigo-600 font-bold">{repeatPct}%</span> of unique customers returned
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Row 4: Peak Hours + Day of Week ── */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-6">
            <Clock className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg font-bold text-gray-900">Peak Hours</h3>
            <span className="ml-auto text-xs text-gray-400">Bills per hour (IST)</span>
          </div>
          <div className="h-56">
            {peakHours.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-400 text-sm">No hourly data.</div>
            ) : (
              <Bar data={peakHoursData} options={peakHoursOptions} />
            )}
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-6">
            <Calendar className="w-5 h-5 text-teal-600" />
            <h3 className="text-lg font-bold text-gray-900">Day of Week</h3>
            <span className="ml-auto text-xs text-gray-400">Revenue by day</span>
          </div>
          <div className="h-56">
            {dayOfWeek.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-400 text-sm">No data.</div>
            ) : (
              <Bar data={dowData} options={dowOptions} />
            )}
          </div>
        </div>
      </div>

      {/* ── Row 5: Floor Utilization ── */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-6">
            <MapPin className="w-5 h-5 text-amber-600" />
            <h3 className="text-lg font-bold text-gray-900">Table / Floor Utilization</h3>
            <span className="ml-auto text-xs text-gray-400">Revenue by section (top 10)</span>
          </div>
          {!floorUtilization || floorUtilization.length === 0 ? (
            <div className="h-40 flex items-center justify-center text-gray-400 text-sm">No dine-in section data for this period.</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
              <div className="h-64">
                <Bar data={floorData} options={floorOptions} />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-gray-100 text-xs font-black text-gray-400 uppercase tracking-wider">
                      <th className="pb-3">Section</th>
                      <th className="pb-3 text-right">Bills</th>
                      <th className="pb-3 text-right">Revenue</th>
                      <th className="pb-3 text-right">Share</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {floorUtilization.map((f, i) => {
                      const totalRev = floorUtilization.reduce((a, b) => a + b.revenue, 0);
                      const share = totalRev > 0 ? Math.round((f.revenue / totalRev) * 100) : 0;
                      return (
                        <tr key={f._id} className="text-sm font-semibold text-gray-700 hover:bg-slate-50/50 transition">
                          <td className="py-2.5 flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-amber-50 flex items-center justify-center text-xs font-bold text-amber-600">{i + 1}</span>
                            {f._id}
                          </td>
                          <td className="py-2.5 text-right text-gray-500">{f.count}</td>
                          <td className="py-2.5 text-right font-mono text-amber-600">₹{f.revenue.toLocaleString()}</td>
                          <td className="py-2.5 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <div className="w-12 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full bg-amber-400 rounded-full" style={{ width: `${share}%` }}></div>
                              </div>
                              <span className="text-xs text-gray-400">{share}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Row 6: Category + Top Products ── */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-6">
            <Layers className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg font-bold text-gray-900">Sales by Category</h3>
          </div>
          <div className="h-72">
            {categoryPerformance.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-400 text-sm">No category data.</div>
            ) : (
              <Bar data={barChartData} options={barChartOptions} />
            )}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-indigo-600" />
              <h3 className="text-lg font-bold text-gray-900">Top-Selling Products</h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-full font-bold">Top 10</span>
              <button
                onClick={() => { window.location.hash = "/product-analytics"; }}
                className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800 hover:underline transition"
              >
                View Detailed Analytics →
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-100 text-xs font-black text-gray-400 uppercase tracking-wider">
                  <th className="pb-3">Product</th>
                  <th className="pb-3 text-right">Qty Sold</th>
                  <th className="pb-3 text-right">Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {topItems.length === 0 ? (
                  <tr><td colSpan="3" className="py-4 text-center text-gray-400 text-sm">No items sold.</td></tr>
                ) : topItems.map((item, i) => (
                  <tr key={item._id} className="text-sm font-semibold text-gray-700 hover:bg-slate-50/50 transition">
                    <td className="py-3 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-gray-500">{i + 1}</span>
                      {item._id}
                    </td>
                    <td className="py-3 text-right font-mono text-gray-900">{item.qtySold}</td>
                    <td className="py-3 text-right font-mono text-indigo-600">₹{item.totalSales.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400 font-semibold">
            <span>Aggregated from POS checkouts</span>
            <span className="flex items-center gap-1">Live updates <ChevronRight className="w-3.5 h-3.5" /></span>
          </div>
        </div>
      </div>

      {/* ── Row 7: Top Customers ── */}
      <div className="max-w-7xl mx-auto bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <div className="flex items-center gap-2 mb-6">
          <Users className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-bold text-gray-900">Top Customers</h3>
          <span className="ml-auto text-xs bg-teal-50 text-teal-700 px-2.5 py-1 rounded-full font-bold">By Spend</span>
        </div>
        {topCustomers.length === 0 ? (
          <p className="text-center text-gray-400 text-sm py-6">No customer data for this period. Add customer mobile numbers at checkout to track them here.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-100 text-xs font-black text-gray-400 uppercase tracking-wider">
                  <th className="pb-3">#</th>
                  <th className="pb-3">Name</th>
                  <th className="pb-3">Mobile</th>
                  <th className="pb-3 text-right">Visits</th>
                  <th className="pb-3 text-right">Total Spent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {topCustomers.map((c, i) => (
                  <tr key={c._id} className="text-sm font-semibold text-gray-700 hover:bg-slate-50/50 transition">
                    <td className="py-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black text-white ${i === 0 ? "bg-amber-400" : i === 1 ? "bg-slate-400" : i === 2 ? "bg-orange-400" : "bg-indigo-100 text-indigo-600"}`}>{i + 1}</span>
                    </td>
                    <td className="py-3">{c.customerName || "—"}</td>
                    <td className="py-3 text-gray-500 font-mono">{c.customerMobile || "—"}</td>
                    <td className="py-3 text-right">{c.billCount}</td>
                    <td className="py-3 text-right font-mono text-teal-600 font-bold">₹{c.totalSpent.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
};

export default AnalyticsDashboard;
